import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { ISeatInfo } from '../../../context/SystemContext/useSeat';
import { isSameJstDate, parseJstDateTime } from '../../../utils/jstDate';

const SeatContent = ({
  data,
  isPreOrder,
  dataFieldBgColor = 'bg-[#E2F6E4]',
  deliveryRequest,
}: {
  data: ISeatInfo;
  isPreOrder: boolean;
  dataFieldBgColor?: string;
  deliveryRequest?: string;
}) => {
  const { t } = useTranslation();
  const email = data.email || '-';
  const phone = data.phone || '-';
  const deliveryRequestText = deliveryRequest === 'WHEN_ITS_READY' ? t('WHEN_ITS_READY') : deliveryRequest || '-';

  const getFormattedDate = () => {
    if (!data.order_date) return '';

    const date = parseJstDateTime(data.order_date);
    const isTodayDate = isSameJstDate(data.order_date);
    const weekdayName = format(date, 'E', { locale: ja });

    if (isTodayDate) {
      return t('TODAY');
    }

    return `${format(date, 'yyyy/MM/dd')} (${weekdayName})`;
  };

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-gray-600">{t('SEAT_INFO_CONFIRM_TEXT')}</p>

      {/* Order Date */}
      <div className={`${dataFieldBgColor} rounded-lg p-4 text-center`}>
        <p className="text-sm text-[#667085] mb-1">{isPreOrder ? t('PREORDER_DATE') : t('ORDER_DATE')}</p>
        <div className="text-3xl text-[#667085] font-semibold">{getFormattedDate()}</div>
      </div>

      {isPreOrder && deliveryRequest && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 break-words">{t('DELIVERY_REQUEST')}</label>
          <div className={`${dataFieldBgColor} rounded-lg p-3 flex-1`}>
            <p className="text-[#667085] font-semibold text-base text-center">{deliveryRequestText}</p>
          </div>
        </div>
      )}

      {/* Area Name */}
      <div className="flex gap-4 items-center">
        <label className="block text-sm font-medium text-[#344054] mb-1 max-w-[70px] break-words">{t('SEAT_TYPE')}</label>
        <div className={`${dataFieldBgColor} rounded-lg p-3 flex-1`}>
          <p className="text-[#667085] font-semibold text-base text-center">{data.seat_type}</p>
        </div>
      </div>

      {/* Area Name */}
      <div className="flex gap-4 items-center">
        <label className="block text-sm font-medium text-[#344054] mb-1 max-w-[70px] break-words">{t('AREA_NAME')}</label>
        <div className={`${dataFieldBgColor} rounded-lg p-3 flex-1`}>
          <p className="text-[#667085] font-semibold text-base text-center">{data.area || '-'}</p>
        </div>
      </div>

      {/* Aisle */}
      <div className="flex gap-4 items-center">
        <label className="block text-sm font-medium text-gray-700 mb-1 max-w-[70px] break-words">{t('AISLE')}</label>
        <div className={`${dataFieldBgColor} rounded-lg p-3 flex-1`}>
          <p className="text-[#667085] font-semibold text-base text-center">{data.aisle || '-'}</p>
        </div>
      </div>

      {/* Additional Info */}
      <div className="flex gap-4 items-center">
        <label className="block text-sm font-medium text-gray-700 mb-1 max-w-[70px] break-words">{t('ADDITIONAL_INFO')}</label>
        <div className={`${dataFieldBgColor} rounded-lg p-3 flex-1`}>
          <p className="text-[#667085] font-semibold text-base text-center">{data.block || '-'}</p>
        </div>
      </div>

      {/* Row and Seat Number */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('ROW')}</label>
          <div className={`${dataFieldBgColor} rounded-lg p-3`}>
            <p className="text-[#667085] font-semibold text-base text-center">{data.row}</p>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('SEAT_NUMBER')}</label>
          <div className={`${dataFieldBgColor} rounded-lg p-3`}>
            <p className="text-[#667085] font-semibold text-base text-center">{data.seat_number}</p>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 break-words">{t('EMAIL')}</label>
        <div className={`${dataFieldBgColor} rounded-lg p-3 flex-1`}>
          <p className="text-[#667085] font-semibold text-base text-center">{email}</p>
        </div>
      </div>

      {isPreOrder && data.phone && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 break-words">{t('PHONE')}</label>
          <div className={`${dataFieldBgColor} rounded-lg p-3 flex-1`}>
            <p className="text-[#667085] font-semibold text-base text-center">{phone}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeatContent;
